/**
 * ExampleModal - Modal de exemplos e dicas para diferentes seções
 * 
 * Extraído do App.js durante refatoração Fase 1
 * 
 * Funcionalidades:
 * - Exibe exemplos de boas práticas para marketplace, vagas, comunidade e profissionais
 * - Portal para renderização fora da hierarquia DOM
 * - Dicas contextualizadas por tipo de conteúdo
 * 
 * Dependências:
 * - ReactDOM.createPortal
 * - useTranslation (traduções)
 * - Card, Button (shadcn/ui)
 */

import React from "react";
import ReactDOM from "react-dom";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { useTranslation } from "../translations";

const ExampleModal = ({ isOpen, onClose, type }) => {
  const { t } = useTranslation();
  
  if (!isOpen) return null;

  const examples = {
    marketplace: {
      title: "Exemplo de Anúncio Bem Feito",
      icon: "🛒",
      example: {
        titulo: "Trator John Deere 5075E - Seminovo",
        preco: "R$ 85.000,00",
        descricao: "Trator em excelente estado, revisado, com apenas 2.000 horas de uso. Ideal para propriedades de seringueira de médio porte."
      },
      tips: [
        "✅ Título claro e específico (marca, modelo, estado)",
        "✅ Preço definido (evite 'a combinar')",
        "✅ Fotos de qualidade mostrando o produto",
        "✅ Descrição detalhada com características",
        "✅ Localização para facilitar logística"
      ]
    },
    vagas: {
      title: "Exemplo de Vaga Bem Feita",
      icon: "💼",
      example: {
        titulo: "Sangrador Experiente - Fazenda São João",
        local: "Votuporanga, SP",
        descricao: "Buscamos sangrador com mínimo 3 anos de experiência para trabalhar em fazenda com 15.000 árvores. Oferecemos moradia e salário acima do mercado."
      },
      tips: [
        "✅ Título com cargo e local",
        "✅ Requisitos claros (experiência, habilidades)",
        "✅ Benefícios oferecidos (moradia, alimentação)",
        "✅ Faixa salarial ou 'a combinar'",
        "✅ Informações de contato"
      ]
    },
    comunidade: {
      title: "Exemplo de Dica Útil",
      icon: "💬",
      example: {
        titulo: "Melhor Horário para Sangria",
        categoria: "Técnica de Sangria",
        descricao: "A sangria realizada nas primeiras horas da manhã (5h-8h) produz até 30% mais látex devido à maior pressão de turgor nas plantas."
      },
      tips: [
        "✅ Título que resume a dica",
        "✅ Categoria correta",
        "✅ Informação prática e aplicável",
        "✅ Base em experiência ou dados",
        "✅ Linguagem simples e direta"
      ]
    },
    profissionais: {
      title: "Como Encontrar o Profissional Ideal",
      icon: "👥",
      example: {
        titulo: "Maria Santos - Sangradora",
        selo: "🥇 Selo Ouro",
        descricao: "8 anos de experiência em sangria. Especializada em clones RRIM 600 e GT 1. Avaliação média de 4.8 estrelas."
      },
      tips: [
        "✅ Use o filtro por Selo de Qualidade para encontrar profissionais certificados",
        "✅ Verifique a especialização (sangrador, agrônomo, etc.)",
        "✅ Veja as avaliações de outros empregadores",
        "✅ Clique em 'Ver Perfil' para mais detalhes",
        "✅ Use 'Contratar' para ver informações de contato e WhatsApp"
      ]
    },
    buscarVagas: {
      title: t('exampleServiceOfferTitle'),
      icon: "👷",
      example: {
        titulo: t('exampleServiceOfferName'),
        local: t('exampleServiceOfferLocation'),
        descricao: t('exampleServiceOfferDesc')
      },
      tips: [
        t('exampleServiceOfferTip1'),
        t('exampleServiceOfferTip2'),
        t('exampleServiceOfferTip3'),
        t('exampleServiceOfferTip4'),
        t('exampleServiceOfferTip5')
      ],
      buttonText: t('exampleServiceOfferButton')
    }
  };

  const content = examples[type] || examples.marketplace;

  return ReactDOM.createPortal(
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4" onClick={onClose}>
      <Card className="w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-bold flex items-center gap-2">
              <span>{content.icon}</span> {content.title}
            </h2>
            <Button variant="ghost" size="sm" onClick={onClose}>✕</Button>
          </div>

          {/* Exemplo Visual */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-green-800 mb-2">{content.example.titulo}</h3>
            {content.example.preco && <p className="text-green-600 font-bold mb-2">{content.example.preco}</p>}
            {content.example.local && <p className="text-gray-600 text-sm mb-2">📍 {content.example.local}</p>}
            <p className="text-gray-700 text-sm">{content.example.descricao}</p>
          </div>

          {/* Dicas */}
          <div className="mb-4">
            <h4 className="font-semibold text-gray-800 mb-2">{t('bestPractices')}</h4>
            <ul className="space-y-1">
              {content.tips.map((tip, i) => (
                <li key={i} className="text-sm text-gray-600">{tip}</li>
              ))}
            </ul>
          </div>

          <Button onClick={onClose} className="w-full bg-green-600 hover:bg-green-700">
            {content.buttonText || (type === 'profissionais' ? 'Entendi, vou buscar!' : 'Entendi, vou criar o meu!')}
          </Button>
        </div>
      </Card>
    </div>,
    document.body
  );
};

export default ExampleModal;
